import { useState, useEffect, useContext } from "react";
import { useDispatch } from "react-redux";
import { ServiceContext, IUser } from "@msx/platform-services";
import axios from "axios";
import { setTimeout } from "timers";
import {
  SETCurrUserData,
  setPreferenceChanged,
  setIsLoader,
} from "../../../../store";
import {
  getHeaderTokenFunc,
  putConsumptionAPI,
  postConsumptionAPI,
} from "../../../../../app/utils/httpUtils";
import { GetTheTileConfiguration } from "../../../../../app/components/TileConfiguration/V2_TileConfigurationHelperFile";
import { FeedbackScreenshotDownloader } from "../../../../../app/components/feedback/FeedbackScreenshotDownloader";
import { PromptGalleryService } from "../../../../../app/components/copilot/PromptGallery/services/promptGalleryService";

/**
 * useUserProfile — Manages user profile data, preferences, and related API calls.
 *
 * Extracted from V2_Shell.tsx (PR #4, Phase 1).
 * Zero logic changes — pure cut-paste extraction.
 *
 * useState moved: currentUserData, userDataCreated, isPageLoad, previousUserData, userDataChanged,
 *                 isDefaultPrefSet, isPresetModal, isProfileLoading, isAmericasPref, isAmericasSMCPref,
 *                 isParamerterLoad, isCanceledPreset, reccomendedData, recommendationLoaded,
 *                 isAiUser, isAiServicesEnabled, isAreaSupportEnabled, isWwicAiEnabled,
 *                 Area, RoleSummary, OrgSummary, OrgDetail, Segment, SubSegment,
 *                 prefrenceRefresh, userWelcomeUpdated, isMCAPSCopilotEnabled, userEmailCalled
 * Functions moved: getUserIDByEmailId, handleSuccessResponse, setReccomendedForUser,
 *                  getDefaultAmericasPref, PresetPreference, isCheckAmericasPref, createUser,
 *                  validateUserIDByEmailId, handleChildStateChange, handleConfirmPreset,
 *                  hideConfirmModel, updateUser
 * Effects moved: #4, #5, #9, #11
 */

interface UseUserProfileParams {
  getAccessTokenForAPICall: any;
  setAccessTokenForAPIcall: (val: any) => void;
  isUserLoggedIn: boolean;
  currentUser: IUser;
  currentUserDbId: number;
  setCurrentUserDbId: (val: any) => void;
  currentUserIsWelcomeMessage: any;
  setCurrentUserIsWelcomeMessage: (val: any) => void;
  finalTileObject: any;
  setFinalTileObject: (val: any) => void;
  handleErrorResponse: (error: any) => void;
  handleNotFoundResponse: () => void;
  setIsOpen: (val: boolean) => void;
  setTheSegmentPopUp: (val: boolean) => void;
}

export const useUserProfile = (params: UseUserProfileParams) => {
  const {
    getAccessTokenForAPICall, setAccessTokenForAPIcall,
    isUserLoggedIn,
    currentUser,
    currentUserDbId, setCurrentUserDbId,
    currentUserIsWelcomeMessage, setCurrentUserIsWelcomeMessage,
    finalTileObject, setFinalTileObject,
    handleErrorResponse, handleNotFoundResponse,
    setIsOpen, setTheSegmentPopUp,
  } = params;

  const context = useContext(ServiceContext);
  const { authClient } = context;
  const reduxDispatch = useDispatch();
  const apiEndPoint = `${process.env.REACT_APP_Consumption_API_ENDPOINT}`;

  const [currentUserData, setCurrentUserData] = useState<any>(undefined);
  const [userDataCreated, setUserDataCreated] = useState(false);
  const [isPageLoad, setIsPageLoad] = useState(true);
  const [previousUserData, setPreviousUserData] = useState(currentUserData);
  const [userDataChanged, setUserDataChanged] = useState(false);
  const [isDefaultPrefSet, setIsDefaultPrefSet] = useState(false);
  const [isPresetModal, setPresetModal] = useState(false);
  const [isProfileLoading, setProfileLoading] = useState(false);
  const [isAmericasPref, setAmericasPref] = useState(false);
  const [isAmericasSMCPref, setAmericasSMCPref] = useState(false);
  const [isParamerterLoad, setParamerterLoad] = useState("");
  const [isCanceledPreset, setCanceledPreset] = useState(false);
  const [reccomendedData, setReccomendedData] = useState([]);
  const [recommendationLoaded, setRecommendationLoaded] = useState(false);
  const [isAiUser, setIsAiUser] = useState(false);
  const [isAiServicesEnabled, setIsAiServicesEnabled] = useState(false);
  const [isAreaSupportEnabled, setIsAreaSupportEnabled] = useState(false);
  const [isWwicAiEnabled, setIsWwicAiEnabled] = useState(false);
  const [Area, setArea] = useState("");
  const [RoleSummary, setRoleSummary] = useState("");
  const [OrgSummary, setOrgSummary] = useState("");
  const [OrgDetail, setOrgDetail] = useState("");
  //   const[Role , setRole] = useState("");
  const [Segment, setSegment] = useState("N/A");
  const [SubSegment, setSubSegment] = useState("N/A");
  const [prefrenceRefresh, setPrefrenceRefresh] = useState(null);
  const [userWelcomeUpdated, setUserWelcomeUpdated] = useState(false);
  const [isMCAPSCopilotEnabled, setIsMCAPSCopilotEnabled] = useState(
    () => sessionStorage.getItem('hasCopilotAccess') === 'true'
  );
  const [userEmailCalled, setUserEmailCalled] = useState(false);

  //  There are two Scenarios from where user can be created,
  //  1. First Time user visits the site and click on close icon/button in the what's new popup.
  //  2. First Time user visits the site via Aks URL then create user API call will get triggered. 
  const createUser = async () => {
    try {
      const apiUrl = '/api/V2_User/Add';
      const postData = {
        UserArea: 0,
        UserADGroupID: [
          {
            userADGroupID: 1,
          },
        ],
        DataverseRowID: "3f5a37dc-8d95-4232-85a7-990dab5888a9",
        IsActive: 1,
        IsWelcomeMessage: false,
      };

      const response = await postConsumptionAPI(apiUrl, postData, context.authClient);
      if (response.status >= 200 && response.status < 300) {
        handleChildStateChange(response.data, false);
      }
    } catch (error) {
      console.error("An error occurred while fetching api.", error);
    }
  };

  // this function is same as getUserIDByEmailId but it returns the user details and getUserIDByEmailId returns void 
  const validateUserIDByEmailId = async () => {
    try {
      const user = await authClient.getUser();
      sessionStorage.setItem("LoggedInUser", user.email)
      if (context?.telemetryClient) {
        const url = `${apiEndPoint}/api/V2_User/GetUserByEmailId/${user.email}/${user.userObject.localAccountId}`;

        const tres = await getHeaderTokenFunc(
          context.authClient,
          getAccessTokenForAPICall
        );

        const headers: any = await getHeaderTokenFunc(
          context.authClient,
          tres.token
        );
        const response = await axios.get(url, {
          headers: headers.request.headers,
        });
        if (response.status === 200) {
          handleSuccessResponse(response.data);
          setUserEmailCalled(true);
          setIsAiUser(response.data.isAiUser);
          setIsAiServicesEnabled(response.data.isAiServicesEnabled);
          setIsAreaSupportEnabled(response.data.isAreaSupportEnabled);
          setIsWwicAiEnabled(response.data.isWwicAiEnabled);
        } else if (response?.status === 404 || response?.status === 204) {
          handleNotFoundResponse();
          setUserEmailCalled(true);
        }
        return response;
      }
      return null;
    } catch (error) {
      handleErrorResponse(error);
      return null;
    }
  };

  //data for 9 useeffect
  const isCheckAmericasPref = () => {
    setProfileLoading(true);
    setPresetModal(false);
    setIsDefaultPrefSet(true);
    finalTileObject.loadForms = true;
  };

  const getUserIDByEmailId = async () => {
    try {
      const user = await authClient.getUser();      
      sessionStorage.setItem("LoggedInUser", user.email)
      if (context?.telemetryClient) {
        const url = `${apiEndPoint}/api/V2_User/GetUserByEmailId/${user.email}/${user.userObject.localAccountId}`;
        const headers: any = await getHeaderTokenFunc(
          context.authClient,
          getAccessTokenForAPICall.token
        );
        const response = await axios.get(url, {
          headers: headers.request.headers,
        });
        if (response.status === 200) {
          handleSuccessResponse(response.data);
          setUserEmailCalled(true);
          setIsAiUser(response.data.isAiUser);
          setIsAiServicesEnabled(response.data.isAiServicesEnabled);
          setIsAreaSupportEnabled(response.data.isAreaSupportEnabled);
          setIsWwicAiEnabled(response.data.isWwicAiEnabled);
        } else if (response?.status === 404 || response?.status === 204) {
          handleNotFoundResponse();
          setUserEmailCalled(true);
        }
      }
    } catch (error) {
      handleErrorResponse(error);
    }
  };

  const handleSuccessResponse = async (data) => {
    // hide the Annoucements
    if (data.userArea === null) {
      setIsOpen(true);
      setTheSegmentPopUp(true);
      setCurrentUserIsWelcomeMessage(data?.isWelcomeMessage);
      setCurrentUserDbId(data.id);
      sessionStorage.setItem("_announcement", data.annoucementKey);
    } else {
      //removed unused setting of state variables
      setAmericasPref(data.isAmerica);
      setAmericasSMCPref(data.isAmericaSMC);
      setCurrentUserDbId(data.id);
      setCurrentUserData(data);
      setCurrentUserIsWelcomeMessage(data?.isWelcomeMessage);
      sessionStorage.setItem("_announcement", data.annoucementKey);
      if (
        finalTileObject["isValidated"] &&
        finalTileObject["isFeedbackUrlDownload"]
      ) {
        const feedbackResult = await FeedbackScreenshotDownloader(
          finalTileObject,
          context,
          isUserLoggedIn
        );
        setFinalTileObject(feedbackResult);
      }
      if (!finalTileObject["isValidated"] && !isParamerterLoad) {
        const finalTile = GetTheTileConfiguration(finalTileObject, context);
        setFinalTileObject(await finalTile);
        setParamerterLoad("true");
      }
      setReccomendedForUser(data.userAreaName, data.orgSummary, data.roleSummary);
      setArea(data.userAreaName);
      setRoleSummary(data.roleSummary);
      setOrgSummary(data.orgSummary);
      setOrgDetail(data.orgDetail);    
      // isMCAPSCopilotEnabled now derived from CopilotADGroupMapping via sessionStorage
      // (set by V2_Tile when copilot access map is fetched)
    }
    reduxDispatch(SETCurrUserData(data));
  };

  const setReccomendedForUser = async (area, userOrgSummary, roleSummary) => {
    try {
      if (!currentUser.email || currentUser.email.trim() === "") {
        return;
      }
      const encodedOrgSummary = encodeURIComponent(userOrgSummary);
      const encodedroleSummary = encodeURIComponent(roleSummary);
      const apiUrl = "/api/V2_UserWorkSpace/SetReccomendedTile";
      const postData = {
        Upn: currentUser.email,
        Area: area,
        userOrgSummary: encodedOrgSummary,
        Role: encodedroleSummary
      };

      const response = await postConsumptionAPI(
        apiUrl,
        postData,
        context.authClient
      );

      if (response.status >= 200 && response.status < 300) {
        setReccomendedData(response.data);
      }
    } catch (error) {
      console.error("An error occurred while fetching the API.", error);
    } finally {
      // Only set loaded to true if we actually attempted the API call
      if (currentUser.email && currentUser.email.trim() !== "") {
        setRecommendationLoaded(true);
      }
    }
  };

  const handleChildStateChange = (newState, check) => {
    // Removed unused state variables
    setTheSegmentPopUp(true);
    setUserDataCreated(true);
    setCurrentUserIsWelcomeMessage(check);
  };

  //segment popup
  const getDefaultAmericasPref = async (action) => {
    try {
      if (context?.telemetryClient) {
        let url = "";
        if (action === "update") {
          setIsDefaultPrefSet(true);
        }
        if (finalTileObject["isAmericaSMC"]) {
          url = `${apiEndPoint}/api/V2_Service/GetDefaultAmericasSMCPref`;
        } else {
          url = `${apiEndPoint}/api/V2_Service/GetDefaultAmericasPref`;
        }
        const headers: any = await getHeaderTokenFunc(
          context.authClient,
          getAccessTokenForAPICall.token
        );
        const response = await axios.get(url, {
          headers: headers.request.headers,
        });
        if (response?.status === 200) {
          sessionStorage.setItem("AreaName", response.data?.areaName);
          sessionStorage.setItem(
            "CheckVisibility",
            response.data?.checkVisibility
          );

          if (action === "update") {
            PresetPreference(response.data?.areaName);
          }
        } else if (response?.status === 404 || response?.status === 204) {
          handleNotFoundResponse();
        }
      }
    } catch (error) {
      handleErrorResponse(error);
    }
  };
  const PresetPreference = async (area) => {
    try {
      const apiUrl = "/api/V2_User/PresetPreference";
      const postData = {
        //UPN: currentUser.email,
        //Oid: currentUser.userObject.localAccountId,
        UserArea: area,
        DataverseRowID: "3f5a37dc-8d95-4232-85a7-990dab5888a9",
        IsActive: 1,
        IsWelcomeMessage: currentUserIsWelcomeMessage,
      };

      const response = await putConsumptionAPI(
        apiUrl,
        postData,
        context.authClient
      );

      if (response.status >= 200 && response.status < 300) {
        setCurrentUserDbId(response.data);
        setPrefrenceRefresh(true);
        reduxDispatch(setPreferenceChanged(false));
        if (finalTileObject["isAmerica"]) {
          setTimeout(() => {
            finalTileObject.loadForms = true;
          }, 500);
        }
        if (finalTileObject["isAmericaSMC"]) {
          setTimeout(() => {
            finalTileObject.loadForms = true;
          }, 500);
        }
      }
    } catch (error) {
      console.error("An error occurred while fetching the API.", error);
    }
  };

  const updateUser = async (area) => {
    try {
      const apiUrl = "/api/V2_User/Update";
      const postData = {
        //UPN: currentUser.email,
        Oid: currentUser.userObject.localAccountId,
        UserArea: area,
        UserADGroupID: [
          {
            userADGroupID: 1,
          },
        ],
        DataverseRowID: "3f5a37dc-8d95-4232-85a7-990dab5888a9",
        IsActive: 1,
        IsWelcomeMessage: currentUserIsWelcomeMessage,
      };

      const response = await putConsumptionAPI(
        apiUrl,
        postData,
        context.authClient
      );

      if (response.status >= 200 && response.status < 300) {
        setCurrentUserDbId(response.data);
        setPrefrenceRefresh(true);
      }
    } catch (error) {
      console.error("An error occurred while fetching the API.", error);
    }
  };

  //routes
  const handleConfirmPreset = () => {
    if (
      (finalTileObject["isAmerica"] || finalTileObject["isAmericaSMC"]) &&
      isDefaultPrefSet === false
    ) {
      setProfileLoading(true);
      setPresetModal(false);
      getDefaultAmericasPref("update");
    }
  };

  const hideConfirmModel = () => {
    setPresetModal(false);
    setCanceledPreset(true);
    // Clear PURL params to prevent tile flow from triggering after "No" click
    window.history.replaceState({}, '', window.location.pathname);
    setProfileLoading(false);
    finalTileObject.isLoaded = true;
    finalTileObject.loadForms = false;
  };

  // Effects

  //4.effect
  useEffect(() => {
    if (currentUserData !== previousUserData) {
      setUserDataChanged(true);
      setPreviousUserData(currentUserData);
    } else {
      setUserDataChanged(false);
    }
  }, [currentUserData]);

  //5.effect
  useEffect(() => {
    if (isUserLoggedIn && currentUser.email !== "") {
      let fetchData = async () => {
        getAccessTokenForAPICall ??
          (setAccessTokenForAPIcall(
            await getHeaderTokenFunc(
              context.authClient,
              getAccessTokenForAPICall
            )
          ));

        if (getAccessTokenForAPICall !== null) {
          //   if (sessionStorage.getItem("AreaName") === null && sessionStorage.getItem("RoleName") === null) { await getDefaultAmericasPref("Get"); }
          if (sessionStorage.getItem("AreaName") === null) {
            await getDefaultAmericasPref("Get");
          }

          await getUserIDByEmailId();         //load default prefrence for Americas

          // Initialize prompt gallery data
          try {
            if (context) {
            
              await PromptGalleryService.initializeGlobalPromptData(context, getAccessTokenForAPICall);
            }
          } catch (error) {
            console.error('❌ Failed to initialize prompt gallery data in V2_Shell:', error);
          }
        }
      };
      fetchData();
    }
  }, [
    context,
    isUserLoggedIn,
    currentUser,
    getAccessTokenForAPICall,
    prefrenceRefresh,
  ]);

  //9.effect
  useEffect(() => {
    if (finalTileObject["isAmerica"] || finalTileObject["isAmericaSMC"]) {
      if (finalTileObject.isLoaded !== true) {
        isCheckAmericasPref();
      }
    }
  }, [
    finalTileObject.isAmerica,
    finalTileObject.isAmericaSMC,
    isAmericasSMCPref,
    isAmericasPref,
    currentUserDbId,
  ]);

  //11.effect
  useEffect(() => {
    const fetchUserData = async () => {
      await getUserIDByEmailId();
      if (userWelcomeUpdated) {
        reduxDispatch(setIsLoader(true));
      }
    };
    if (userDataCreated === true || userWelcomeUpdated) {
      fetchUserData();
    }
  }, [userDataCreated, userWelcomeUpdated]);

  return {
    currentUserData,
    userDataCreated,
    isPageLoad, setIsPageLoad,
    userDataChanged,
    isDefaultPrefSet,
    isPresetModal,
    isProfileLoading, setProfileLoading,
    isAmericasPref, isAmericasSMCPref,
    isParamerterLoad, setParamerterLoad,
    isCanceledPreset,
    reccomendedData, recommendationLoaded, setRecommendationLoaded,
    isAiUser, isAiServicesEnabled, isAreaSupportEnabled, isWwicAiEnabled,
    isMCAPSCopilotEnabled, setIsMCAPSCopilotEnabled,
    Area, RoleSummary, OrgSummary, OrgDetail,
    Segment, setSegment, SubSegment, setSubSegment,
    prefrenceRefresh, setPrefrenceRefresh,
    userWelcomeUpdated, setUserWelcomeUpdated,
    userEmailCalled, setUserEmailCalled,
    getUserIDByEmailId,
    validateUserIDByEmailId,
    getDefaultAmericasPref,
    createUser,
    handleChildStateChange,
    handleConfirmPreset,
    hideConfirmModel,
    updateUser,
  };
};
